import * as React from 'react';
import {MercuryCache} from '../context/MercuryCache';
import {Mercury} from '../context/Mercury';
import {useTimer} from '../../shared-common/hooks/useTimer';
import {isEmpty, isNil, mean, max, takeRight} from 'lodash-es';
import {MercuryQuery} from '../types/MercuryQuery';
import {MercuryResult} from '../types/MercuryResult';
import {PropsWithChildren, useCallback, useRef} from 'react';
import {useForceUpdate} from '../../shared-common/hooks/useForceUpdate';
import {useConstant} from '../../shared-ui/hooks/useConstant';
import {MercuryQueryHelpers} from '../helpers/MercuryQueryHelpers';
import {useChangeHandler} from '../../shared-ui/hooks/useChangeHandler';
import {useBusyTimer} from '../../shared-ui/hooks/useBusyTimer';
import {DataResponse} from '../../shared/models/DataResponse';
import DataService, {FetchOperation} from '../../shared/services/DataService';

export interface MercuryEndpointProps {
  location: string;
}

interface BatchResponse {
  results: unknown[];
  timings: number[];
}

export const MercuryEndpoint = (props: PropsWithChildren<MercuryEndpointProps>) => {
  const MAXIMUM_CONCURRENT_QUERIES = 10;
  const AUTO_REFRESH_FREQUENCY_MILLISECONDS = 10;
  const TOP_LEVEL_LOCATION_REGEX = new RegExp('^\\/([A-Za-z\\-]*)');
  const QUERY_BATCH_WINDOW_SIZE = 10;

  const pendingQueries = useConstant<Map<string, MercuryQuery>>(new Map<string, MercuryQuery>());
  const loadingQueries = useConstant<Map<string, MercuryQuery>>(new Map<string, MercuryQuery>());
  const processedQueries = useConstant<Map<string, MercuryResult>>(new Map<string, MercuryResult>());
  const forceUpdate = useForceUpdate();
  const currentQueryCountRef = useRef<number>(0);
  const currentQueryBatchSizeRef = useRef<number>(1);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const averageQueryTimeWindowRef = useRef<number[]>([]);

  async function loadDataOrThrow<T>(url: string,
                                    requestBody?: any,
                                    abortSignal?: AbortSignal): Promise<DataResponse<T> | undefined> {
    try {
      return await DataService.fetch<T>(url, requestBody, FetchOperation.Post, undefined, abortSignal);
    } catch (err) {
      if (!isNil(abortSignal) && abortSignal.aborted) {
        return undefined;
      }
      throw err;
    }
  }

  const checkForUnprocessedQueries = (): boolean => {
    return pendingQueries.size > 0 || loadingQueries.size > 0;
  };

  const dynamicallyAdjustBatchSize = (response: BatchResponse) => {
    if (isNil(response)) {
      return;
    }

    averageQueryTimeWindowRef.current.push(...response.timings);
    averageQueryTimeWindowRef.current = takeRight(averageQueryTimeWindowRef.current, QUERY_BATCH_WINDOW_SIZE);

    let averageTiming = mean(averageQueryTimeWindowRef.current);
    let maxTiming = max(averageQueryTimeWindowRef.current);

    if (isNaN(averageTiming)) {
      return;
    }

    if (averageTiming >= 6000) {
      currentQueryBatchSizeRef.current = 1;
    } else if (maxTiming > 1000 || averageTiming >= 5000) {
      currentQueryBatchSizeRef.current = 6;
    } else if (averageTiming >= 3000) {
      currentQueryBatchSizeRef.current = 20;
    } else if (averageTiming >= 500) {
      currentQueryBatchSizeRef.current = 30;
    } else {
      currentQueryBatchSizeRef.current = 60;
    }
  };

  const executePendingQueries = async (batchSize?: number): Promise<void> => {
    const queryBatch: {
      hash: string,
      query: MercuryQuery
    } [] = [];

    if (!isNil(batchSize) && currentQueryCountRef.current >= MAXIMUM_CONCURRENT_QUERIES) {
      return;
    }

    for (let entry of pendingQueries.entries()) {
      const hash = entry[0];
      const pendingQuery = entry[1];

      queryBatch.push({
        hash: hash,
        query: pendingQuery
      });

      loadingQueries.set(hash, pendingQuery);
      pendingQueries.delete(hash);

      if (!isNil(batchSize) && queryBatch.length >= batchSize) {
        break;
      }
    }

    if (queryBatch.length === 0) {
      return;
    }

    try {
      currentQueryCountRef.current++;

      let response: DataResponse<BatchResponse> | undefined;

      try {
        response = await loadDataOrThrow<BatchResponse>(
          '/mercury/execute-query-batch',
          queryBatch.map(e => e.query),
          abortControllerRef.current.signal
        );
      } catch {
        // If the request fails for whatever reason, fail the entire batch
        for (let i = 0; i < queryBatch.length; i++) {
          const hash = queryBatch[i].hash;
          processedQueries.set(
            hash,
            {
              availability: 'failed',
              value: undefined
            }
          );
          loadingQueries.delete(hash);
        }
      }

      if (isEmpty(response)) {
        for (let i = 0; i < queryBatch.length; i++) {
          loadingQueries.delete(queryBatch[i].hash);
        }
        return;
      }

      dynamicallyAdjustBatchSize(response.data);

      for (let i = 0; i < response.data.results.length; i++) {
        const hash = queryBatch[i].hash;

        const isAnonymized =
          isNil(response.data.results[i]) // Legacy approach kept around to not break cached query results
          || response.data.results[i].hasOwnProperty('_')
          && (response.data.results[i] as { _: string })._ === 'anonymized';

        const hasNoResponses = response.data.results[i] === 0;
        const hasFailed =
          !isNil(response.data.results[i])
          && response.data.results[i].hasOwnProperty('_')
          && (response.data.results[i] as { _: string })._ === 'error';

        if (isAnonymized) {
          processedQueries.set(
            hash,
            {
              availability: 'insufficientResponses',
              value: undefined
            }
          );
        } else if (hasNoResponses) {
          processedQueries.set(
            hash,
            {
              availability: 'noResponses',
              value: response.data.results[i]
            }
          );
          // Allow for failing of individual queries
        } else if (hasFailed) {
          processedQueries.set(
            hash,
            {
              availability: 'failed',
              value: undefined
            }
          );
        } else {
          processedQueries.set(
            hash,
            {
              availability: 'available',
              value: response.data.results[i]
            }
          );
        }

        loadingQueries.delete(hash);
      }

    } finally {
      if (!checkForUnprocessedQueries()) {
        forceUpdate();
      }

      currentQueryCountRef.current--;
      onBusy();
    }
  };

  useTimer(AUTO_REFRESH_FREQUENCY_MILLISECONDS, () => executePendingQueries(currentQueryBatchSizeRef.current), []);

  const {onBusy} = useBusyTimer(10000, 'Mercury queries');

  const executeQuery = useCallback(
    (query: MercuryQuery): MercuryResult => {

      onBusy();

      if (isEmpty(query.where)) {
        query.where = undefined;
      }

      const queryHash = MercuryQueryHelpers.hash(query);

      // If this query is already in the cache, return the query result immediately. The result is always returned
      // immediately - the availability field of the result will show the loading status of the result.
      const processedQuery = processedQueries.get(queryHash);
      if (!isNil(processedQuery)) {
        return processedQuery;
      }

      const isPending = pendingQueries.has(queryHash);
      if (isPending) {
        return {
          availability: 'pending',
          value: undefined
        };
      }

      const isLoading = loadingQueries.has(queryHash);
      if (isLoading) {
        return {
          availability: 'loading',
          value: undefined
        };
      }

      const hasExistingPendingQueries = checkForUnprocessedQueries();

      // The query is not in the cache, so add it, and return a 'pending' result so long.
      pendingQueries.set(queryHash, query);

      // If no existing queries were pending before the new query was executed, we probably need to update
      // the UI to show loading indicators
      if (!hasExistingPendingQueries) {
        forceUpdate();
      }

      return {
        availability: 'pending',
        value: undefined
      };
    },
    []
  );

  const flushCache = useCallback(
    () => {
      // Forget all cached query results
      processedQueries.clear();
    },
    []
  );

  const executeAllPendingQueries = useCallback(
    async (): Promise<void> => {
      // Keep processing pending queries until there are none left
      while (checkForUnprocessedQueries()) {
        // Process one batch of pending queries
        await executePendingQueries(currentQueryBatchSizeRef.current);
        // Return control to the Browser event loop briefly so that pending events can be processed.
        // This prevents the browser from deciding that the webpage has stopped responding.
        await new Promise<void>(resolve => {
          setTimeout(resolve, 0);
        });
      }
    },
    []
  );

  const cancelPendingQueries = useCallback(
    () => {
      // Abort any running queries
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      loadingQueries.clear();
      currentQueryCountRef.current = 0;

      // Cancel any pending queries
      pendingQueries.clear();
    },
    [abortControllerRef]
  );

  // Flush the cache when navigating between top-level pages
  useChangeHandler(props.location, (previousLocation: string) => {
    cancelPendingQueries();

    const previousTopLevelLocation = previousLocation.match(TOP_LEVEL_LOCATION_REGEX)[1];
    const newTopLevelLocation = props.location.match(TOP_LEVEL_LOCATION_REGEX)[1];

    if (previousTopLevelLocation !== newTopLevelLocation) {
      flushCache();
      currentQueryBatchSizeRef.current = 1;
    }
  });

  // Expose the Mercury and MercuryCache contexts to downstream components
  return (
    <MercuryCache.Provider
      value={{
        flushCache: flushCache,
        executeAllPendingQueries: executeAllPendingQueries,
        hasPendingQueries: checkForUnprocessedQueries(),
        cancelPendingQueries: cancelPendingQueries
      }}
    >
      <Mercury.Provider
        value={{
          executeQuery: executeQuery
        }}
      >
        {props.children}
      </Mercury.Provider>
    </MercuryCache.Provider>
  );
};
